import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import {
  Plus, Search, Edit, Eye, EyeOff, PiggyBank, ScrollText, Send, X, ArrowDownCircle, ArrowUpCircle
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const formatCurrency = (amount) =>
  `$${Number(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const PoolManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPot, setEditingPot] = useState(null);
  const [ledgerPot, setLedgerPot] = useState(null);
  const [disbursePot, setDisbursePot] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pots-management', searchTerm],
    queryFn: () => api.get(`/pots?search=${searchTerm}&includeInactive=true&limit=100`).then(res => res.data),
    keepPreviousData: true,
    staleTime: 15000
  });

  const pots = data?.pots || [];

  const invalidate = () => {
    queryClient.invalidateQueries(['pots-management']);
    queryClient.invalidateQueries(['pots']);
  };

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/pots', payload),
    onSuccess: () => { toast.success('Pozo creado'); invalidate(); setShowCreateForm(false); },
    onError: (e) => toast.error(e.response?.data?.message || 'Error creando el pozo')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => api.put(`/pots/${id}`, payload),
    onSuccess: () => { toast.success('Pozo actualizado'); invalidate(); setEditingPot(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Error actualizando el pozo')
  });

  const toggleMutation = useMutation({
    mutationFn: (pot) => api.put(`/pots/${pot.id}`, {
      name: pot.name, commissionRate: pot.commission_rate, isActive: !pot.is_active
    }),
    onSuccess: (_r, pot) => { toast.success(`Pozo ${pot.is_active ? 'desactivado' : 'activado'}`); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.message || 'Error actualizando el pozo')
  });

  const handleToggle = (pot) => {
    if (window.confirm(`¿${pot.is_active ? 'Desactivar' : 'Activar'} el pozo "${pot.name}"? Un pozo inactivo deja de cobrar en los empates.`)) {
      toggleMutation.mutate(pot);
    }
  };

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error cargando pozos</h3>
        <p className="text-red-600">{error.response?.data?.message || 'No se pudieron cargar los pozos'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestión de Pozos</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Cada pozo descuenta un % de las ganancias de los eventos asociados y acumula fondos para reinvertir.
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" /> Nuevo Pozo
        </button>
      </div>

      {/* Filtro */}
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar pozos por nombre..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-600 dark:text-gray-400">Cargando pozos...</div>
        ) : pots.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {searchTerm ? `No se encontraron pozos para "${searchTerm}"` : 'No hay pozos registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pozo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">% Comisión</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Saldo acumulado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                {pots.map(pot => (
                  <tr key={pot.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 mr-3">
                          <PiggyBank className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{pot.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{Number(pot.commission_rate)}%</td>
                    <td className="px-6 py-4 text-sm font-semibold text-success-600 dark:text-success-400">{formatCurrency(pot.balance)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pot.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {pot.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-3">
                        <button onClick={() => setDisbursePot(pot)} title="Desembolsar"
                          className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400">
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => setLedgerPot(pot)} title="Ver movimientos"
                          className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400">
                          <ScrollText className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingPot(pot)} title="Editar"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleToggle(pot)} title={pot.is_active ? 'Desactivar' : 'Activar'}
                          className={pot.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}>
                          {pot.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateForm && (
        <PotFormModal
          title="Crear Nuevo Pozo"
          onSubmit={(d) => createMutation.mutate(d)}
          onClose={() => setShowCreateForm(false)}
          isLoading={createMutation.isLoading}
        />
      )}
      {editingPot && (
        <PotFormModal
          title="Editar Pozo"
          pot={editingPot}
          onSubmit={(d) => updateMutation.mutate({ id: editingPot.id, ...d })}
          onClose={() => setEditingPot(null)}
          isLoading={updateMutation.isLoading}
        />
      )}
      {ledgerPot && <PotLedgerModal pot={ledgerPot} onClose={() => setLedgerPot(null)} />}
      {disbursePot && (
        <PotDisburseModal
          pot={disbursePot}
          onClose={() => setDisbursePot(null)}
          onDone={() => { invalidate(); queryClient.invalidateQueries(['clients']); queryClient.invalidateQueries(['pot-ledger', disbursePot.id]); }}
        />
      )}
    </div>
  );
};

// ---- Modal crear/editar ----
const PotFormModal = ({ title, pot, onSubmit, onClose, isLoading }) => {
  const [formData, setFormData] = useState({
    name: pot?.name || '',
    commissionRate: pot?.commission_rate ?? '',
    isActive: pot?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: formData.name.trim(),
      commissionRate: parseFloat(formData.commissionRate),
      isActive: formData.isActive
    });
  };

  const rateValid = formData.commissionRate !== '' && Number(formData.commissionRate) >= 0 && Number(formData.commissionRate) <= 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre del pozo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Porcentaje de comisión (%) *</label>
              <input
                type="number" min="0" max="100" step="0.01"
                value={formData.commissionRate}
                onChange={(e) => setFormData(p => ({ ...p, commissionRate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Se descuenta de la ganancia (profit) en cada empate, adicional a la comisión de la plataforma.</p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox" id="potActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="potActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">Pozo activo</label>
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={onClose} disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50">Cancelar</button>
              <button type="submit" disabled={isLoading || !formData.name.trim() || !rateValid}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {isLoading ? 'Guardando...' : pot ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---- Modal ledger ----
const PotLedgerModal = ({ pot, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['pot-ledger', pot.id],
    queryFn: () => api.get(`/pots/${pot.id}/ledger?limit=100`).then(r => r.data)
  });
  const ledger = data?.ledger || [];
  const typeLabel = { collection: 'Cobro', disbursement_player: 'Desembolso a jugador', disbursement_event: 'Reparto a evento' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-dark-600 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Movimientos · {pot.name}</h3>
            <p className="text-sm text-success-600 dark:text-success-400 font-semibold">Saldo: {formatCurrency(pot.balance)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Cargando...</div>
          ) : ledger.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Sin movimientos todavía</div>
          ) : (
            <div className="space-y-2">
              {ledger.map(row => {
                const positive = Number(row.amount) >= 0;
                return (
                  <div key={row.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-dark-700">
                    <div className="flex items-center space-x-3">
                      {positive
                        ? <ArrowDownCircle className="w-5 h-5 text-success-500" />
                        : <ArrowUpCircle className="w-5 h-5 text-danger-500" />}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{typeLabel[row.type] || row.type}</p>
                        <p className="text-xs text-gray-500">
                          {row.event_name ? `Evento: ${row.event_name}` : ''}{row.user_name ? ` · ${row.user_name}` : ''}
                        </p>
                        {row.description && <p className="text-xs text-gray-400 italic">{row.description}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${positive ? 'text-success-600' : 'text-danger-600'}`}>
                        {positive ? '+' : ''}{formatCurrency(row.amount)}
                      </p>
                      <p className="text-xs text-gray-400">Saldo: {formatCurrency(row.balance_after)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Modal desembolso ----
const PotDisburseModal = ({ pot, onClose, onDone }) => {
  const [mode, setMode] = useState('player'); // 'player' | 'event'
  const [amount, setAmount] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');

  const { data: usersData } = useQuery({
    queryKey: ['pot-disburse-users', userSearch],
    queryFn: () => api.get(`/users?search=${userSearch}&limit=10`).then(r => r.data),
    enabled: mode === 'player'
  });
  const users = usersData?.users || [];

  const { data: eventsData } = useQuery({
    queryKey: ['pot-disburse-events'],
    queryFn: () => api.get('/events').then(r => r.data),
    enabled: mode === 'event'
  });
  const events = eventsData?.events || (Array.isArray(eventsData) ? eventsData : []);

  const disburseMutation = useMutation({
    mutationFn: (payload) => {
      const url = mode === 'player' ? `/pots/${pot.id}/disburse/player` : `/pots/${pot.id}/disburse/event`;
      return api.post(url, payload);
    },
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Desembolso aplicado');
      onDone();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || e.response?.data?.error || 'Error en el desembolso')
  });

  const amountNum = parseFloat(amount);
  const balance = Number(pot.balance);
  const amountValid = amountNum > 0 && amountNum <= balance;
  const canSubmit = amountValid && (mode === 'player' ? !!selectedUserId : !!selectedEventId);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (mode === 'player') disburseMutation.mutate({ userId: parseInt(selectedUserId), amount: amountNum });
    else disburseMutation.mutate({ eventId: parseInt(selectedEventId), amount: amountNum });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Desembolsar · {pot.name}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm text-success-600 dark:text-success-400 font-semibold mb-4">Disponible: {formatCurrency(pot.balance)}</p>

          <div className="flex rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden mb-4">
            <button type="button" onClick={() => setMode('player')}
              className={`flex-1 py-2 text-sm font-medium ${mode === 'player' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300'}`}>
              A un jugador
            </button>
            <button type="button" onClick={() => setMode('event')}
              className={`flex-1 py-2 text-sm font-medium ${mode === 'event' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300'}`}>
              A un evento
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'player' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Jugador *</label>
                <input
                  type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Buscar cliente por nombre o email..."
                  className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-dark-500 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                />
                <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100" required>
                  <option value="">Seleccionar jugador...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Evento *</label>
                <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100" required>
                  <option value="">Seleccionar evento...</option>
                  {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">El monto se reparte equitativamente entre los participantes activos del evento.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto *</label>
              <input
                type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                required
              />
              {amount !== '' && !amountValid && (
                <p className="text-xs text-danger-500 mt-1">
                  {amountNum <= 0 ? 'El monto debe ser mayor a 0' : 'El monto supera el saldo del pozo'}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={onClose} disabled={disburseMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50">Cancelar</button>
              <button type="submit" disabled={!canSubmit || disburseMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {disburseMutation.isLoading ? 'Procesando...' : 'Confirmar desembolso'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PoolManagement;
